import React, { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, frames } from "../../../Styles";
import { SecondaryPopover, StationLoading, StationLocationVisual } from "../..";
import { currentEsfViewTypes, useEsfContext } from "../_EsfWalkThrough/EsfContext";
import { selectStationByLocationId, useStationById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationSlice";
import { Station } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/queryStations";
import { useAttentionLog } from "../../../Services";
import { useAppSelector } from "../../../Store";
import { StationLocationVisualWalkIn } from "../StationLayouts/esfStationVisualWalkIn";

interface PenBasic_Props {
  stationId: number;
  gridArea: string;
  focus_view?: boolean;
  index: number;
  popoverContent?: JSX.Element;
  overrides?: {
    primaryColor?: colors;
    textColor?: colors;
    width?: string;
    border?: string;
    attention?: number
  }
}

export default function ESF_STATION({ stationId, index, focus_view, popoverContent, overrides }: PenBasic_Props) {
  const { t } = useTranslation();
  const {isLoading, entity: station} = useStationById(stationId);
  const { state: { selectedStations, currentStationViewType, connection, domain, system }, dispatch } = useEsfContext();
    
  // Styles
  const _primaryColor = overrides && overrides.primaryColor ? overrides.primaryColor : colors.greyLightSecondary
  const _textColor = overrides && overrides.textColor ? overrides.textColor : colors.greyDarkPrimary
  const _width = overrides && overrides.width ? overrides.width : "80px"
  const _border = overrides && overrides.border ? overrides.border : "none"
    
  // #region | Attentions
  const { data: attentionLogState, call: getAttentionLogs } = useAttentionLog(connection);

  useEffect(() => {
    if (system?.system_id) {
      getAttentionLogs({ ids: [system.system_id] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const logData = useMemo(() => {
    if (!system) return [];
    if (domain in attentionLogState && attentionLogState[domain] && typeof attentionLogState[domain] === "object" && "data" in attentionLogState[domain] && attentionLogState[domain].data && system?.system_id) {
      return attentionLogState[domain].data.filter((log) => log.fk_systems === system?.system_id && log.dismissed !== true);
    }
    return []
  }, [attentionLogState, domain, system?.system_id])

  const stationAttentionLevel = useMemo(() => {
    if (!station) return 0;
  
    const levels = logData
      .filter(log => log.fk_locations === station.locationId)
      .filter(log => station.terminals?.includes(log.fk_terminals ? log.fk_terminals : -1))
      .map(log => log.level);
  
    return levels.length ? Math.max(...levels) : undefined;
  }, [logData, station]);
  // #endregion

  // #region | SystemControle / On station click
  const handleClick = () => {
    const alreadySelected = selectedStations?.some((p) => p.station_id === station?.station_id);

    let newSelectedList;

    if (alreadySelected) {
      newSelectedList = selectedStations?.filter((p) => p.station_id !== station?.station_id);
    } else {
      newSelectedList = [...(selectedStations ?? []), station] as Station[];
    }

    dispatch({ selectedStations: newSelectedList });
  }
  // #endregion

  const Pen = () => {
    if (isLoading) {
      return <StationLoading/>;
    }
    if (station === undefined) {
      return <div>{"Station not found"}</div>
    }
    const gridArea = station.visual ? station.visual.replace(/^"|"$/g, '').replace(/^'|'$/g, '').split("-")[0] : ""    
    // const _popoverContent = typeof popoverContent === "function" ? popoverContent(index, station) : popoverContent ?? undefined;
    
    const systemControleByStationTypePopover = (station.stationType === 2
      ? <StationLocationVisual isSelected={selectedStations && selectedStations.some(s => s === station)} key={station.station_id} attention={overrides && overrides.attention} onClick={handleClick} content={station?.name ?? ""} gridArea={gridArea} locationVisual={Number((station?.visual ?? "").split("-")[2])} />
      : <StationLocationVisualWalkIn isSelected={selectedStations && selectedStations.some(s => s === station)} key={station.station_id} attention={overrides && overrides.attention} onClick={handleClick} content={station?.name ?? ""} gridArea={gridArea} locationVisual={Number((station?.visual ?? "").split("-")[2])} />
    )

    const systemControleByStationType = (station.stationType === 2
      ? <StationLocationVisual isSelected={selectedStations && selectedStations.some(s => s === station)} key={station.station_id} attention={overrides && overrides.attention} onClick={handleClick} content={station?.name ?? ""} gridArea={gridArea} locationVisual={Number((station?.visual ?? "").split("-")[2])} />
      : <StationLocationVisualWalkIn isSelected={selectedStations && selectedStations.some(s => s === station)} key={station.station_id} attention={overrides && overrides.attention} onClick={handleClick} content={station?.name ?? ""} gridArea={gridArea} locationVisual={Number((station?.visual ?? "").split("-")[2])} />
    )

    const attentionByStationType = (station.stationType === 2
      ? <StationLocationVisual key={station.station_id} isSelected attention={stationAttentionLevel} content={station?.name ?? ""} gridArea={gridArea} locationVisual={Number((station?.visual ?? "").split("-")[2])} />
      : <StationLocationVisualWalkIn key={station.station_id} isSelected attention={stationAttentionLevel} content={station?.name ?? ""} gridArea={gridArea} locationVisual={Number((station?.visual ?? "").split("-")[2])} />
    )

    const basicByStationType = (station.stationType === 2
      ? <StationLocationVisual isSelected content={station?.name ?? ""} gridArea={gridArea} locationVisual={Number((station?.visual??"").split("-")[2])}/>
      : <StationLocationVisualWalkIn isSelected content={station?.name ?? ""} gridArea={gridArea} locationVisual={Number((station?.visual??"").split("-")[2])}/>
    )
    
    switch (currentStationViewType) {
      case currentEsfViewTypes.systemControle:
        return (
          <div>  
            { popoverContent ?
              <SecondaryPopover
                content={
                  <div style={{}}> {systemControleByStationTypePopover}  </div>
                }
                popoverHeader=""
                popoverContent={<>{popoverContent}</>}
                overrides={{ primaryColor: colors.greyDarkPrimary }}
              />
            : systemControleByStationType }
          </div>
        );
      case currentEsfViewTypes.attention:
        return (
          attentionByStationType
        );
      case currentEsfViewTypes.basic:
      default:
        return (
          basicByStationType
        )
    }
  }

  return Pen();
}